/**
 * Swap-identity token client.
 *
 * Swap-service calls carry a per-user identity token so the swap service can
 * scope its data to the caller's organization. That token is minted by the
 * gateway, not the swap service, and it must not ride in the Authorization
 * header: in production the reverse proxy rewrites the swap path and injects its
 * own service-to-service Authorization bearer, which would overwrite anything we
 * put there. The identity token therefore rides in a dedicated header,
 * X-Swap-Identity, attached by the swap request path in swapApi.
 *
 * This module owns fetching and caching that token. It requests one from the
 * gateway with the normal user bearer, holds it in memory only, and refreshes it
 * before it expires. It is never persisted: it is a short-lived derived
 * credential, and a reload simply mints a fresh one on the next swap call.
 */

import { request } from './api';

/** The gateway response from the swap session-token endpoint. */
interface SwapSessionTokenResponse {
  /** The minted swap-identity JWT. */
  token: string;
  /** Lifetime in seconds from the moment the gateway issued it. */
  expires_in: number;
  /** Token type discriminant, expected to be "swap-identity". */
  token_type?: string;
}

/**
 * A cached token together with the wall-clock time it stops being usable. The
 * expiry is computed at fetch time from the response's expires_in so the refresh
 * decision does not depend on decoding the JWT.
 */
interface CachedSwapToken {
  /** The swap-identity JWT to send in X-Swap-Identity. */
  token: string;
  /** Milliseconds since the epoch after which the token is considered expired. */
  expiresAtMillis: number;
}

/**
 * The in-memory cache. Null until the first successful fetch and after any
 * invalidation. Held in a module variable rather than the store so the swap
 * request path can read it without a framework dependency, matching how the
 * session module holds the access token.
 */
let cached: CachedSwapToken | null = null;

/**
 * A single in-flight fetch, shared by concurrent swap calls so a burst of swap
 * requests mints one token rather than racing several. Cleared when the fetch
 * settles.
 */
let inFlight: Promise<string> | null = null;

/**
 * Refresh when fewer than this many seconds of lifetime remain. The contract
 * sets the refresh threshold at thirty seconds so a swap call never sends a
 * token that is about to expire mid-request.
 */
const REFRESH_LEEWAY_SECONDS = 30;

/**
 * Whether a cached token is still usable for the leeway window. A token missing
 * or within the leeway of expiry is treated as needing a refresh so the swap
 * call carries one with headroom.
 *
 * @param entry The cached token, or null when none is held.
 * @param nowMillis The current time in milliseconds since the epoch.
 * @returns True when the cached token can be sent as-is.
 */
export function isFreshEnough(entry: CachedSwapToken | null, nowMillis: number): boolean {
  if (!entry) return false;
  return entry.expiresAtMillis - nowMillis > REFRESH_LEEWAY_SECONDS * 1000;
}

/**
 * Compute the absolute expiry for a freshly minted token from the response's
 * lifetime. A missing or non-positive lifetime is treated as immediately
 * expiring so a malformed response never yields a token that looks fresh
 * forever; the next swap call then mints a new one.
 *
 * @param expiresInSeconds The lifetime the gateway reported.
 * @param issuedAtMillis The time the response was received.
 * @returns The absolute expiry in milliseconds since the epoch.
 */
export function computeExpiry(expiresInSeconds: number, issuedAtMillis: number): number {
  const ttl = Number.isFinite(expiresInSeconds) && expiresInSeconds > 0 ? expiresInSeconds : 0;
  return issuedAtMillis + ttl * 1000;
}

/**
 * Return a usable swap-identity token, minting or refreshing as needed. Reuses a
 * cached token while it has more than the leeway of lifetime left; otherwise it
 * fetches a fresh one from the gateway under a single-flight guard so concurrent
 * swap calls do not each mint their own.
 *
 * @param signal Optional abort signal, forwarded to the mint request.
 * @returns The swap-identity JWT to place in the X-Swap-Identity header.
 * @throws {ApiError} When the gateway cannot mint a token, so a swap call fails
 *   closed rather than proceeding without an identity token.
 */
export async function getSwapIdentityToken(signal?: AbortSignal): Promise<string> {
  if (isFreshEnough(cached, Date.now())) {
    // The non-null assertion is safe: isFreshEnough returns false for a null
    // entry, so reaching here means the cache holds a usable token.
    return (cached as CachedSwapToken).token;
  }
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<string> => {
    const res = await request<SwapSessionTokenResponse>('/v1/auth/swap-token', {
      method: 'POST',
      body: {},
      // A mint is a fresh short-lived credential, so a transient failure may
      // retry like other idempotent requests; a stale mint has no side effect.
      retry: true,
      cache: 'no-store',
      signal,
    });
    const token = typeof res.token === 'string' ? res.token.trim() : '';
    if (token === '') {
      // A response without a token is unusable. Leave the cache untouched and
      // surface the failure so the swap call fails closed.
      throw new Error('swap session-token response carried no token');
    }
    cached = {
      token,
      expiresAtMillis: computeExpiry(res.expires_in, Date.now()),
    };
    return token;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/**
 * Drop any cached swap-identity token. Called on logout so a token minted for
 * one session is never reused after the session ends. The next swap call mints a
 * fresh one bound to the new session.
 */
export function clearSwapIdentityToken(): void {
  cached = null;
  inFlight = null;
}
